"use client"

import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  Button,
  FormField,
  Textarea,
} from '@yeshuman/sdk'
import type {
  ConversationalBriefFields,
  ConversationalBriefPlugin,
  ConversationalBriefState,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { toast } from 'sonner'

import { useCreateBrief, type Brief } from '../../hooks/use-briefs'
import { structuredBriefToCreateData } from '../../lib/conversational-brief-map'

import './conversational-brief-host.css'

export type ConversationalBriefCreateProps = {
  plugin: ConversationalBriefPlugin
  onClose: () => void
  onSaved: (brief: Brief) => void
  onUseForm: () => void
}

export function ConversationalBriefCreate({
  plugin,
  onClose,
  onSaved,
  onUseForm,
}: ConversationalBriefCreateProps) {
  const { createBrief, isCreating } = useCreateBrief()
  const [opening, setOpening] = useState('')
  const [draft, setDraft] = useState('')
  const [state, setState] = useState<ConversationalBriefState | null>(null)
  const [values, setValues] = useState<ConversationalBriefFields>({})
  const [saved, setSaved] = useState(false)

  const Panel = plugin.ConversationPanel
  const Editor = plugin.BriefEditor
  const startPlaceholder =
    plugin.copy?.startPlaceholder ?? 'Describe the role in plain language…'
  const ready =
    state != null && (state.status === 'ready' || state.brief.status === 'ready')

  const persistProductBrief = (fields: ConversationalBriefFields) => {
    if (state) {
      plugin.saveBrief(state.brief.id, fields)
    }
    createBrief(structuredBriefToCreateData(fields), {
      onSuccess: (created) => {
        setSaved(true)
        toast.success('Brief created')
        onSaved(created)
      },
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to create brief')
      },
    })
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <ArtifactCanvasBody className="min-h-0 flex-1 space-y-6 overflow-y-auto">
        <div className="conversational-brief-host" data-conversational-brief="host">
          {state == null ? (
            <form
              data-conversational-brief="start"
              onSubmit={(event) => {
                event.preventDefault()
                const text = opening.trim()
                if (!text) {
                  toast.error('Describe the role to start')
                  return
                }
                const next = plugin.start(text)
                setState(next)
                setValues(next.brief.fields)
                setOpening('')
              }}
            >
              <FormField
                label="Describe the role"
                description="Talk in plain language. I will ask clarifying questions and challenge vague or biased phrasing."
              >
                <Textarea
                  value={opening}
                  onChange={(event) => setOpening(event.target.value)}
                  placeholder={startPlaceholder}
                  rows={5}
                />
              </FormField>
              <div className="mt-4">
                <Button type="submit">Start conversation</Button>
              </div>
            </form>
          ) : (
            <>
              <Panel
                state={state}
                draft={draft}
                onDraftChange={setDraft}
                onReply={(message) => {
                  const next = plugin.reply(state.id, message)
                  setState(next)
                  setValues(next.brief.fields)
                }}
                onPause={() => setState(plugin.pause(state.id))}
                onResume={() => setState(plugin.resume(state.id))}
                onRefine={(message) => {
                  const next = plugin.refine(state.brief.id, message)
                  setState(next)
                  setValues(next.brief.fields)
                  setSaved(false)
                }}
              />
              {ready ? (
                <Editor
                  brief={state.brief}
                  values={values}
                  saved={saved}
                  onChange={(field, value) => {
                    setValues((current) => ({ ...current, [field]: value }))
                    setSaved(false)
                  }}
                  onSave={persistProductBrief}
                />
              ) : null}
            </>
          )}
        </div>
      </ArtifactCanvasBody>
      <ArtifactCanvasFooterBar
        leading={
          <Button type="button" variant="ghost" onClick={onUseForm} disabled={isCreating}>
            Use form instead
          </Button>
        }
        actions={
          <Button type="button" variant="outline" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
        }
      />
    </div>
  )
}
